import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppContext } from '../../libs/appContextLib';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useRouteRegistry } from '../../queries/useRouteRegistry';
import { useUserRoutePreferences } from '../../queries/useUserRoutePreferences';
import { useFetchRecipes } from '../../queries/useFetchRecipes';
import { useFetchRecentExecutions } from '../../queries/useFetchRecentExecutions';
import { useFetchGtmStatsCustomConfig } from '../../queries/useFetchGtmStatsCustomConfig';
import { useNavigationContextStandalone } from '../../hooks/useNavigationContext';
import {
  getVisibleRoutes,
} from '../../libs/navigationLogic';
import {
  transformRecipePinToRoute,
  findLatestExecutionId,
} from '../../libs/recipePinTransformer';
import {resolveRouteViewPin} from '../../libs/navigationPinUtils';
import { MdMenu } from 'react-icons/md';
import { RecipeDesignerIcon } from '../Icons/RecipeDesignerIcon';
import { RecipeResultsIcon } from '../Icons/RecipeResultsIcon';
import { ConfigIcon } from '../Icons/ConfigIcon';
import { LuBeaker } from 'react-icons/lu';

// Import Phase 2 components
import HeroNavSection from './HeroNavSection';
import PinnedRoutes from './PinnedRoutes';
import CurrentPageIndicator from './CurrentPageIndicator';
import DatasetDiscovery from './DatasetDiscovery';
import { standardNewBizStats } from '../../containers/GoalSets/standardNewBizStats';
import { standardUpsellStats } from '../../containers/GoalSets/standardUpsellStats';
import { standardRenewalStats } from '../../containers/GoalSets/standardRenewalStats';

const GTM_STATS_PATH = '/gtm-stats';

const chartIdToLabel = (chartId = '') => {
  return chartId
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const toVisibleBoolean = (value, fallback) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') return value.toLowerCase() !== 'false';
  return Boolean(value);
};

const getAcceptedBookingMetricString = (dataStatus) => {
  const acceptedBookingMetric = dataStatus && dataStatus.booking_metric;
  return acceptedBookingMetric === 'amount_field' || acceptedBookingMetric === 'tcv_field' ? 'Amount' : 'ARR';
};

const getChartScope = (chart, fallback = 'new') => {
  if (chart?.tab) return chart.tab;
  if (chart?.id?.startsWith('upsell')) return 'upsell';
  if (chart?.id === 'activeAccounts') return 'renewal';
  return fallback;
};

const buildChartSearchKeywords = (chart = {}, label = '') => {
  const values = [
    chart.id,
    label,
    chart.title,
    chart.launcherTitle,
    chart.props?.chartTitle,
    chart.props?.chartColorId,
    chart.props?.legendLabel,
    chart.props?.qPilotChartName,
  ].filter(Boolean);

  return [...new Set(values.flatMap((value) => {
    const normalizedValue = String(value).trim().toLowerCase();
    const compactValue = normalizedValue.replace(/\s+/g, '');
    return compactValue && compactValue !== normalizedValue
      ? [normalizedValue, compactValue]
      : [normalizedValue];
  }))];
};

const getChartSearchLabel = (chart = {}, scope = 'new') => {
  const label = chart.launcherTitle || chart.title || chartIdToLabel(chart.id);
  if (scope === 'upsell' && !/upsell|expansion/i.test(label)) {
    return `Upsell ${label}`;
  }
  return label;
};

const isGtmStatsSearchChart = (chart = {}) => {
  if (!chart.id || !chart.title) return false;
  if (chart.props?.isGoalMetricOnly) return false;
  if (chart.props?.gtmStatsCustomConfigEntry === 'arr') return false;

  const scope = getChartScope(chart);
  if (scope === 'upsell' && !chart.id.startsWith('upsell')) return false;

  return true;
};

const getStandardGtmStatsCharts = ({ currUser, dataStatus }) => {
  const acceptedBookingMetricString = getAcceptedBookingMetricString(dataStatus);
  const statArgs = {
    mqlLabel: 'MQL',
    salLabel: 'SAL',
    currUser,
    acceptedBookingMetricString,
  };

  const newBizCharts = standardNewBizStats(statArgs) || [];
  const upsellCharts = standardUpsellStats(statArgs) || [];
  const renewalCharts = standardRenewalStats(statArgs) || [];

  return [
    ...newBizCharts.map((chart) => ({ ...chart, tab: getChartScope(chart, 'new') })),
    ...upsellCharts.map((chart) => ({ ...chart, tab: getChartScope(chart, 'upsell') })),
    ...renewalCharts.map((chart) => ({ ...chart, tab: getChartScope(chart, 'renewal') })),
  ].filter(isGtmStatsSearchChart);
};

const getDefaultGtmChartVisibility = (chart = {}) => {
  if (chart.id === 'quotaAttainment') {
    return process.env.REACT_APP_STAGE === 'dev';
  }
  return chart.visible !== false;
};

const chartToSearchTab = (chart = {}) => {
  const value = chart.id;
  const scope = getChartScope(chart);
  const label = getChartSearchLabel(chart, scope);

  return {
    id: chart.id,
    value,
    label,
    path: `${GTM_STATS_PATH}#${chart.id}`,
    keywords: buildChartSearchKeywords(chart, label),
  };
};

const buildGtmStatsSearchTabs = (chartConfig = [], standardCharts = []) => {
  const chartEntries = Array.isArray(chartConfig) ? chartConfig : [];
  const standardChartIds = new Set(standardCharts.map((chart) => chart.id));

  const configuredVisibleUnknownTabs = chartEntries
    .filter((entry) => entry?.chartId && !standardChartIds.has(entry.chartId))
    .filter((entry) => toVisibleBoolean(entry.visible, false))
    .map((entry) => ({
      id: entry.chartId,
      value: entry.chartId,
      label: chartIdToLabel(entry.chartId),
      path: `${GTM_STATS_PATH}#${entry.chartId}`,
      keywords: [entry.chartId, chartIdToLabel(entry.chartId).toLowerCase()],
    }));

  const knownTabs = standardCharts
    .filter(getDefaultGtmChartVisibility)
    .map(chartToSearchTab);

  return [...knownTabs, ...configuredVisibleUnknownTabs];
};

const mergeRouteTabs = (existingTabs = [], dynamicTabs = []) => {
  const mergedTabs = [...(existingTabs || []), ...(dynamicTabs || [])];
  const deduped = new Map();

  mergedTabs.forEach((tab) => {
    if (!tab?.value || !tab?.label) return;
    const key = `${tab.value}::${tab.label}`;

    if (!deduped.has(key)) {
      deduped.set(key, tab);
      return;
    }

    const existing = deduped.get(key);
    deduped.set(key, {
      ...existing,
      keywords: [...new Set([...(existing.keywords || []), ...(tab.keywords || [])])],
    });
  });

  return Array.from(deduped.values());
};

const ACTIVITY_LABELS_SEARCH_ROUTE = {
  id: 'manage-task-labels',
  path: '/manage-task-labels',
  label: 'Activity Labels',
  icon: ConfigIcon,
  category: 'Setup',
  tags: ['activity labels', 'task labels', 'label setup', 'activity configuration'],
  searchAliases: [
    'activity labels',
    'task labels',
    'manage task labels',
    'task label setup',
    '/manage-task-labels',
  ],
  description: 'Create and manage activity labels and task label mappings',
  isPermanent: false,
  priority: 999,
};

const SUPPLEMENTAL_SEARCH_ROUTES = [
  ACTIVITY_LABELS_SEARCH_ROUTE,
];

const routeExists = (routes, searchRoute) => routes.some(
  (route) => route?.id === searchRoute.id || route?.path === searchRoute.path
);

const getHashTargetFromPath = (path = '') => {
  const hashIndex = String(path).indexOf('#');
  if (hashIndex === -1) return '';

  return String(path).slice(hashIndex + 1).split(/[?&]/)[0];
};

const decodeHashTarget = (target = '') => {
  try {
    return decodeURIComponent(target);
  } catch (_error) {
    return target;
  }
};

const highlightElement = (element) => {
  if (!element) return undefined;

  const previousStyles = {
    backgroundColor: element.style.backgroundColor,
    borderRadius: element.style.borderRadius,
    boxShadow: element.style.boxShadow,
    outline: element.style.outline,
    outlineOffset: element.style.outlineOffset,
    transition: element.style.transition,
  };

  element.style.transition = [
    previousStyles.transition,
    'background-color 180ms ease',
    'box-shadow 180ms ease',
    'outline-color 180ms ease',
  ].filter(Boolean).join(', ');
  element.style.backgroundColor = 'rgba(77, 166, 133, 0.08)';
  element.style.borderRadius = previousStyles.borderRadius || '8px';
  element.style.boxShadow = '0 0 0 3px rgba(77, 166, 133, 0.24), 0 12px 32px rgba(77, 166, 133, 0.16)';
  element.style.outline = '2px solid rgba(77, 166, 133, 0.55)';
  element.style.outlineOffset = '3px';

  return () => {
    element.style.backgroundColor = previousStyles.backgroundColor;
    element.style.borderRadius = previousStyles.borderRadius;
    element.style.boxShadow = previousStyles.boxShadow;
    element.style.outline = previousStyles.outline;
    element.style.outlineOffset = previousStyles.outlineOffset;
    element.style.transition = previousStyles.transition;
  };
};

/**
 * QNavigation - New Navigation Orchestrator
 *
 * Main navigation component that integrates all Phase 2 components
 * and applies business logic from Phase 3.
 *
 * Features:
 * - Hero section (Recipe Designer/Results)
 * - Search with fuzzy matching
 * - Pinned routes
 * - Contextual suggestions
 * - Discoverable routes panel
 * - Role-based visibility
 * - Customer-level route settings
 *
 * Props:
 * - drawerOpen: boolean - Whether sidebar is expanded
 * - drawerWidth: number - Width of drawer
 * - isMobile: boolean - Mobile view
 * - showQPilotChat: boolean - QPilot chat visible
 * - handleShowQPilotChatChange: function - Toggle QPilot
 */
function QNavigation({
  drawerOpen,
  drawerWidth,
  isMobile,
  showQPilotChat,
  handleShowQPilotChatChange,
}) {
  const { currUser, dataStatus, coldStartCommandPaletteOpen = false } = useAppContext();
  const history = useHistory();
  const location = useLocation();

  // Navigation context (tracks active recipe, current page, etc.)
  const _navContext = useNavigationContextStandalone();

  // Fetch route registry (enriched with knowledgebase data)
  const {
    routes: allRoutes,
    knowledgebase: _knowledgebase, // Already fetched in useRouteRegistry - no duplicate call
    isLoading: _isLoadingRoutes,
  } = useRouteRegistry();

  // Fetch user preferences (pins, recents)
  const {
    pinnedRoutes: pinnedRouteIds,
    recentRoutes: _recentRoutes,
    togglePin,
    trackRecentRoute,
  } = useUserRoutePreferences();

  // Fetch recipes for recipe count (only when drawer is open)
  const { data: recipesData } = useFetchRecipes({ enabled: drawerOpen });

  // Fetch recent executions for status badges (only when drawer is open)
  const { data: recentExecutionsData } = useFetchRecentExecutions({ enabled: drawerOpen });

  // Fetch GTM chart visibility so Discoverable search can surface enabled chart views
  const { data: gtmStatsCustomConfig } = useFetchGtmStatsCustomConfig();

  // Get current path for highlighting
  const currentPath = location.pathname;
  const _currentRouteId = currentPath.split('/')[1] || '';
  const [discoverablePopoverAnchor, setDiscoverablePopoverAnchor] = React.useState(null);
  const [globalSearchOpen, setGlobalSearchOpen] = React.useState(false);
  const [globalSearchKey, setGlobalSearchKey] = React.useState(0);
  const [hashHighlightKey, setHashHighlightKey] = React.useState(0);
  const isDiscoverablePopoverOpen = Boolean(discoverablePopoverAnchor);

  React.useEffect(() => {
    if (drawerOpen) {
      setDiscoverablePopoverAnchor(null);
    }
  }, [drawerOpen]);

  const openGlobalSearch = React.useCallback(() => {
    setDiscoverablePopoverAnchor(null);
    setGlobalSearchKey((key) => key + 1);
    setGlobalSearchOpen(true);
  }, []);

  const closeGlobalSearch = React.useCallback(() => {
    setGlobalSearchOpen(false);
  }, []);

  useKeyboardShortcuts({
    onSearchFocus: openGlobalSearch,
    onEscape: closeGlobalSearch,
    enabled: !coldStartCommandPaletteOpen,
  });

  React.useEffect(() => {
    if (!location.hash) {
      return undefined;
    }

    const targetId = getHashTargetFromPath(location.hash);
    if (!targetId) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    let restoreHighlight = null;
    let attemptCount = 0;

    const scrollAndHighlightTarget = () => {
      if (cancelled) return;

      const targetElement = document.getElementById(decodeHashTarget(targetId));
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });

        restoreHighlight = highlightElement(targetElement);
        timeoutId = window.setTimeout(() => {
          if (restoreHighlight) {
            restoreHighlight();
          }
        }, 2200);
        return;
      }

      if (attemptCount < 20) {
        attemptCount += 1;
        timeoutId = window.setTimeout(scrollAndHighlightTarget, 100);
      }
    };

    timeoutId = window.setTimeout(scrollAndHighlightTarget, 0);

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (restoreHighlight) {
        restoreHighlight();
      }
    };
  }, [location.hash, location.key, hashHighlightKey]);

  const standardGtmStatsCharts = React.useMemo(() => {
    return getStandardGtmStatsCharts({ currUser, dataStatus });
  }, [currUser, dataStatus]);

  const gtmStatsSearchTabs = React.useMemo(() => {
    return buildGtmStatsSearchTabs(gtmStatsCustomConfig?.chartConfig, standardGtmStatsCharts);
  }, [gtmStatsCustomConfig?.chartConfig, standardGtmStatsCharts]);

  // Compute visible routes based on role, customer settings, and pins
  const visibleRoutes = React.useMemo(() => {
    if (!currUser || !allRoutes.length) return [];

    const baseVisibleRoutes = getVisibleRoutes(
      currUser,
      dataStatus?.routePreferences || {}, // Use actual route preferences from backend
      { pinnedRoutes: pinnedRouteIds },
      allRoutes
    );

    const routesWithDynamicTabs = baseVisibleRoutes.map((route) => {
      if (route.id !== 'gtm-stats') {
        return route;
      }

      return {
        ...route,
        tabs: mergeRouteTabs(route.tabs, gtmStatsSearchTabs),
      };
    });

    return SUPPLEMENTAL_SEARCH_ROUTES.reduce((routes, searchRoute) => (
      routeExists(routes, searchRoute) ? routes : [...routes, searchRoute]
    ), routesWithDynamicTabs);
  }, [currUser, pinnedRouteIds, allRoutes, dataStatus?.routePreferences, gtmStatsSearchTabs]);

  // Get pinned items (routes + recipe paths)
  const allPinnedItems = React.useMemo(() => {
    const items = [];
    const recipes = recipesData?.recipes || [];
    const recentExecutions = recentExecutionsData?.executions || [];

    pinnedRouteIds.forEach((pinId) => {
      // Check if it's a recipe path
      if (pinId.startsWith('recipe-results/')) {
        const recipeId = pinId.replace('recipe-results/', '');
        const recipe = recipes.find((r) => r.recipe_id === recipeId);

        if (recipe) {
          // Find latest SUCCESSFUL execution for this recipe
          const latestExecution = recentExecutions
            .filter((e) => e.recipe_id === recipeId && e.status === 'completed')
            .sort((a, b) => {
              const dateA = new Date(a.completed_at || a.started_at || 0);
              const dateB = new Date(b.completed_at || b.started_at || 0);
              return dateB - dateA;
            })[0];

          // Create minimal pin data for transformer
          // Use recipe's last_execution data as fallback if not in recent executions list
          const pinData = {
            id: `recipe-pin-${recipeId}`,
            recipeId,
            recipeName: recipe.name,
            recipeCategory: recipe.category,
            lastExecutionAt: latestExecution?.completed_at || recipe.last_execution_at,
            lastExecutionStatus: latestExecution?.status || recipe.last_execution_status,
          };

          items.push(transformRecipePinToRoute(pinData, recipe, latestExecution));
        }
      } else {
        const routeViewPin = resolveRouteViewPin(pinId, visibleRoutes);
        if (routeViewPin) {
          items.push(routeViewPin);
          return;
        }

        // Regular route
        const route = visibleRoutes.find((r) => r.id === pinId);
        if (route) {
          items.push(route);
        }
      }
    });

    return items;
  }, [pinnedRouteIds, visibleRoutes, recipesData, recentExecutionsData]);

  // Get current route for "Currently Viewing" indicator
  // Only show if: route exists, not pinned, not hero
  const currentRoute = React.useMemo(() => {
    const route = visibleRoutes.find((r) =>
      currentPath === r.path || currentPath.startsWith(r.path + '/')
    );

    // Check if current path is a recipe that's pinned
    const recipePathMatch = currentPath.match(/^\/recipe-results\/([^/]+)/);
    const isRecipePinned = recipePathMatch && pinnedRouteIds.includes(`recipe-results/${recipePathMatch[1]}`);

    // Don't show if it's a hero route or already pinned
    if (route?.isPermanent || pinnedRouteIds.includes(route?.id) || isRecipePinned) {
      return null;
    }

    return route;
  }, [visibleRoutes, currentPath, pinnedRouteIds]);

  // Get discoverable routes (not pinned, not hero)
  const discoverableRoutes = React.useMemo(() => {
    return visibleRoutes.filter(
      (r) => !r.isPermanent && !pinnedRouteIds.includes(r.id)
    );
  }, [visibleRoutes, pinnedRouteIds]);

  // Search should include all visible non-permanent routes (including pinned)
  const searchableRoutes = React.useMemo(() => {
    return visibleRoutes.filter((r) => !r.isPermanent);
  }, [visibleRoutes]);

  const isDiscoverableRouteActive = React.useMemo(() => {
    return discoverableRoutes.some((route) =>
      currentPath === route.path || currentPath.startsWith(route.path + '/')
    );
  }, [discoverableRoutes, currentPath]);

  // Calculate recipe stats for hero section
  const activeRecipeCount = React.useMemo(() => {
    return recipesData?.recipes?.length || 0;
  }, [recipesData]);

  const runningExecutionCount = React.useMemo(() => {
    if (!recentExecutionsData?.executions) return 0;
    return recentExecutionsData.executions.filter(
      (e) => e.status === 'running' || e.status === 'pending'
    ).length;
  }, [recentExecutionsData]);

  const recentExecutionCount = React.useMemo(() => {
    return recentExecutionsData?.executions?.length || 0;
  }, [recentExecutionsData]);

  const resolveSearchNavigationPath = React.useCallback((path, route) => {
    if (!path) return path;

    const matchedTabValue = route?.matchedTab?.value;
    const matchedTabPath = route?.matchedTab?.path;
    const routePath = route?.path;
    if (matchedTabPath && routePath && path === routePath) {
      return matchedTabPath;
    }
    if (matchedTabValue && routePath && path === routePath) {
      return `${routePath}/${matchedTabValue}`;
    }

    return path;
  }, []);

  // Navigation handler
  const handleNavigate = (path, route, event) => {
    const resolvedPath = resolveSearchNavigationPath(path, route);
    const resolvedHashTarget = getHashTargetFromPath(resolvedPath);

    // Check for Command (Mac) or Ctrl (Windows/Linux) key
    const isNewTab = event?.metaKey || event?.ctrlKey;

    if (isNewTab) {
      // Open in new window
      window.open(resolvedPath, '_blank', 'noopener,noreferrer');
      closeGlobalSearch();
      return;
    }

    // Handle recipe pin navigation - resolve to latest execution
    if (route?.type === 'recipe-pin') {
      const recipeId = route.metadata.recipeId;
      const recentExecutions = recentExecutionsData?.executions || [];
      const latestExecutionId = findLatestExecutionId(recentExecutions, recipeId);

      if (latestExecutionId) {
        history.push(`/recipe-results/${recipeId}/${latestExecutionId}`);
      } else {
        // Fallback: show execution list
        history.push(`/recipe-results/${recipeId}`);
      }
    } else {
      // Regular route navigation
      const routeId = (resolvedPath.split('/')[1] || '').split(/[?#]/)[0];
      if (routeId && !routeId.startsWith('recipe-')) {
        trackRecentRoute.mutate(routeId);
      }

      history.push(resolvedPath);
    }

    closeGlobalSearch();

    if (resolvedHashTarget) {
      setHashHighlightKey((key) => key + 1);
    }

    // Close QPilot on mobile when navigating
    if (isMobile && showQPilotChat && handleShowQPilotChatChange) {
      handleShowQPilotChatChange();
    }
  };

  // Pin/unpin handler for all items (routes and recipe paths)
  const handleTogglePin = (itemId) => {
    console.log('[QNavigation] handleTogglePin called with:', itemId);

    // Convert recipe-pin-{recipeId} format to recipe-results/{recipeId} path
    if (itemId.startsWith('recipe-pin-')) {
      const recipeId = itemId.replace('recipe-pin-', '');
      togglePin.mutate(`recipe-results/${recipeId}`);
    } else {
      // Regular route pin
      togglePin.mutate(itemId);
    }
  };

  // Reorder pins handler (for drag-drop)
  const handleReorderPins = (newOrder) => {
    // TODO: Implement reordering logic
    // For now, the order is determined by the pinnedRoutes array order
    console.log('Reorder pins:', newOrder);
  };

  const handleOpenDiscoverablePopover = (event) => {
    setDiscoverablePopoverAnchor(event.currentTarget);
  };

  const handleCloseDiscoverablePopover = () => {
    setDiscoverablePopoverAnchor(null);
  };

  const handleDiscoverableNavigate = (path, route, event) => {
    handleNavigate(path, route, event);
    setDiscoverablePopoverAnchor(null);
  };

  const globalSearchDialog = (
    <Dialog
      open={globalSearchOpen}
      onClose={closeGlobalSearch}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          alignSelf: 'flex-start',
          mt: { xs: 4, sm: 8 },
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(39, 41, 49, 0.22)',
        },
      }}
    >
      <Box sx={{ pt: 1, pb: 0.5 }}>
        <DatasetDiscovery
          key={`global-search-${globalSearchKey}`}
          datasets={discoverableRoutes}
          searchDatasets={searchableRoutes}
          onNavigate={handleNavigate}
          onPin={handleTogglePin}
          currentPath={currentPath}
          pinnedDatasetIds={pinnedRouteIds}
          defaultExpanded
          enableSearchPreview={false}
          onNavigateComplete={closeGlobalSearch}
          searchPlaceholder="Type a driver, dataset, account, or deal name..."
        />
      </Box>
    </Dialog>
  );

  // Don't block rendering on loading state - render with static routes immediately
  // Knowledgebase enrichment happens progressively in background
  // if (isLoadingRoutes) {
  //   return (
  //     <Box sx={{ p: 2, textAlign: 'center' }}>
  //       Loading navigation...
  //     </Box>
  //   );
  // }

  // Expanded drawer view
  if (drawerOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 0,
        }}
      >
        {/* Hero Section - Recipe Designer/Results */}
        <Box sx={{ pt: 2 }}>
          <HeroNavSection
            activeRecipeCount={activeRecipeCount}
            runningExecutionCount={runningExecutionCount}
            recentExecutionCount={recentExecutionCount}
            onNavigate={handleNavigate}
            currentPath={currentPath}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Pinned Datasets */}
        <PinnedRoutes
          routes={allPinnedItems}
          onNavigate={handleNavigate}
          onUnpin={handleTogglePin}
          onReorder={handleReorderPins}
          currentPath={currentPath}
          currentHash={location.hash}
          maxPins={10}
        />

        {/* Currently Viewing - Shows active page if not pinned */}
        {currentRoute && (
          <>
            <Divider sx={{ my: 1 }} />
            <CurrentPageIndicator
              route={currentRoute}
              onNavigate={handleNavigate}
              onPin={handleTogglePin}
            />
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Unified Dataset Discovery (replaces NavigationSearch + DiscoverableRoutes) */}
        {discoverableRoutes.length > 0 && (
          <DatasetDiscovery
            datasets={discoverableRoutes}
            searchDatasets={searchableRoutes}
            onNavigate={handleNavigate}
            onPin={handleTogglePin}
            currentPath={currentPath}
            pinnedDatasetIds={pinnedRouteIds}
          />
        )}
        {globalSearchDialog}
      </Box>
    );
  }

  // Collapsed drawer view (icon-only mode)
  const isDesignerActive = currentPath === '/recipe-designer' || currentPath.startsWith('/recipe-designer/');
  const isResultsActive = currentPath === '/recipe-results' || currentPath.startsWith('/recipe-results/');
  const isExploreActive = isDiscoverablePopoverOpen || isDiscoverableRouteActive;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 2,
        height: '100%',
        gap: 1,
      }}
    >
      {/* Recipe Designer Icon */}
      <Tooltip
        title={`Recipe Designer${activeRecipeCount > 0 ? ` (${activeRecipeCount} active)` : ''}`}
        placement="right"
        slotProps={{
          tooltip: {
            sx: { fontSize: '0.875rem' }
          }
        }}
      >
        <IconButton
          onClick={(e) => handleNavigate('/recipe-designer', null, e)}
          sx={{
            width: 44,
            height: 44,
            position: 'relative',
            backgroundColor: isDesignerActive ? 'rgba(77, 166, 133, 0.08)' : 'transparent',
            borderRadius: '4px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '25%',
              bottom: '25%',
              width: 3,
              backgroundColor: isDesignerActive ? '#4DA685' : 'transparent',
              borderRadius: '0 1.5px 1.5px 0',
              transition: 'backgroundColor 0.2s ease',
            },
            '&:hover': {
              backgroundColor: 'rgba(77, 166, 133, 0.12)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            '& > div': {
              minWidth: 'unset !important',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          }}>
            <RecipeDesignerIcon
              color={isDesignerActive ? '#4DA685' : '#6B7280'}
              size={20}
            />
          </Box>
        </IconButton>
      </Tooltip>

      {/* Recipe Results Icon */}
      <Tooltip
        title={`Recipe Results${runningExecutionCount > 0 ? ` (${runningExecutionCount} running)` : recentExecutionCount > 0 ? ` (${recentExecutionCount} recent)` : ''}`}
        placement="right"
        slotProps={{
          tooltip: {
            sx: { fontSize: '0.875rem' }
          }
        }}
      >
        <IconButton
          onClick={(e) => handleNavigate('/recipe-results', null, e)}
          sx={{
            width: 44,
            height: 44,
            position: 'relative',
            backgroundColor: isResultsActive ? 'rgba(77, 166, 133, 0.08)' : 'transparent',
            borderRadius: '4px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '25%',
              bottom: '25%',
              width: 3,
              backgroundColor: isResultsActive ? '#4DA685' : 'transparent',
              borderRadius: '0 1.5px 1.5px 0',
              transition: 'backgroundColor 0.2s ease',
            },
            '&:hover': {
              backgroundColor: 'rgba(77, 166, 133, 0.12)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            '& > div': {
              minWidth: 'unset !important',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          }}>
            <RecipeResultsIcon
              color={isResultsActive ? '#4DA685' : '#6B7280'}
              size={20}
            />
          </Box>
        </IconButton>
      </Tooltip>

      {/* Divider */}
      {allPinnedItems.length > 0 && (
        <Box sx={{ width: '60%', my: 1 }}>
          <Divider />
        </Box>
      )}

      {/* Pinned Items (icon-only) */}
      {allPinnedItems.map((item) => {
        const isRecipePin = item.type === 'recipe-pin';
        const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
        const Icon = isRecipePin ? LuBeaker : item.icon;

        return (
          <Tooltip
            key={item.id}
            title={item.label}
            placement="right"
            slotProps={{
              tooltip: {
                sx: { fontSize: '0.875rem' }
              }
            }}
          >
            <IconButton
              onClick={(e) => handleNavigate(item.path, item, e)}
              sx={{
                width: 44,
                height: 44,
                position: 'relative',
                backgroundColor: isActive ? 'rgba(77, 166, 133, 0.08)' : 'transparent',
                borderRadius: '4px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '25%',
                  bottom: '25%',
                  width: 3,
                  backgroundColor: isActive ? '#4DA685' : 'transparent',
                  borderRadius: '0 1.5px 1.5px 0',
                  transition: 'backgroundColor 0.2s ease',
                },
                '&:hover': {
                  backgroundColor: 'rgba(77, 166, 133, 0.12)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                '& > div': {
                  minWidth: 'unset !important',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              }}>
                {isRecipePin ? (
                  <LuBeaker size={20} color={isActive ? '#4DA685' : '#6B7280'} />
                ) : (
                  <Icon size={20} color={isActive ? '#4DA685' : '#6B7280'} />
                )}
              </Box>
            </IconButton>
          </Tooltip>
        );
      })}

      {discoverableRoutes.length > 0 && (
        <Box
          sx={{
            mt: 'auto',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: 2,
            gap: 1,
          }}
        >
          <Box sx={{ width: '60%', my: 1 }}>
            <Divider />
          </Box>

          <Tooltip
            title={`Explore (${discoverableRoutes.length} data sets)`}
            placement="right"
            slotProps={{
              tooltip: {
                sx: { fontSize: '0.875rem' }
              }
            }}
          >
            <IconButton
              onClick={handleOpenDiscoverablePopover}
              sx={{
                width: 44,
                height: 44,
                position: 'relative',
                backgroundColor: isExploreActive ? 'rgba(77, 166, 133, 0.08)' : 'transparent',
                borderRadius: '4px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '25%',
                  bottom: '25%',
                  width: 3,
                  backgroundColor: isExploreActive ? '#4DA685' : 'transparent',
                  borderRadius: '0 1.5px 1.5px 0',
                  transition: 'backgroundColor 0.2s ease',
                },
                '&:hover': {
                  backgroundColor: 'rgba(77, 166, 133, 0.12)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <MdMenu size={20} color={isExploreActive ? '#4DA685' : '#6B7280'} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Popover
        open={isDiscoverablePopoverOpen}
        anchorEl={discoverablePopoverAnchor}
        onClose={handleCloseDiscoverablePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: drawerWidth || 420,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'calc(100vh - 40px)',
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 12px 28px rgba(39, 41, 49, 0.16)',
              borderRadius: '8px',
            },
          },
        }}
      >
        <DatasetDiscovery
          datasets={discoverableRoutes}
          searchDatasets={searchableRoutes}
          onNavigate={handleDiscoverableNavigate}
          onPin={handleTogglePin}
          currentPath={currentPath}
          pinnedDatasetIds={pinnedRouteIds}
          defaultExpanded
          enableSearchPreview={false}
        />
      </Popover>
      {globalSearchDialog}
    </Box>
  );
}

// Memoize to prevent re-renders from parent updates
// Only re-render when these specific props change
export default React.memo(QNavigation, (prevProps, nextProps) => {
  return (
    prevProps.drawerOpen === nextProps.drawerOpen &&
    prevProps.drawerWidth === nextProps.drawerWidth &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.showQPilotChat === nextProps.showQPilotChat
    // Intentionally exclude handleShowQPilotChatChange - function reference doesn't matter
  );
});
